import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import axiosInstance from "config/axiosInstance";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import WaOutlineButton from "./WaOutlineButton";
import { useAppAlert } from "hooks/useAppAlert";

export default function TemplatesPanel() {
  const { showAlert, AlertBanner } = useAppAlert();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", template: "", activo: true });
  const [preview, setPreview] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/whatsapp/templates");
      if (res.data.success) {
        setTemplates(res.data.templates || []);
      }
    } catch (err) {
      showAlert(err.response?.data?.message || "Error cargando templates", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      nombre: tpl.nombre || "",
      template: tpl.template || "",
      activo: Boolean(tpl.activo),
    });
    setPreview("");
  };

  const closeEdit = () => {
    setEditing(null);
    setPreview("");
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      const res = await axiosInstance.put(`/whatsapp/templates/${editing.id}`, {
        nombre: form.nombre,
        template: form.template,
        activo: form.activo ? 1 : 0,
      });
      if (res.data.success) {
        showAlert("Template actualizado", "success");
        closeEdit();
        fetchTemplates();
      }
    } catch (err) {
      showAlert(err.response?.data?.message || "Error guardando template", "error");
    }
  };

  const handlePreview = async () => {
    if (!editing) return;
    setPreviewLoading(true);
    try {
      const res = await axiosInstance.post(`/whatsapp/templates/${editing.id}/preview`, {
        nombre: "Juan Perez",
        monto: "25.50",
      });
      if (res.data.success) {
        setPreview(res.data.preview);
      }
    } catch (err) {
      showAlert(err.response?.data?.message || "Error en preview", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const toggleActive = async (tpl) => {
    try {
      await axiosInstance.put(`/whatsapp/templates/${tpl.id}`, { activo: tpl.activo ? 0 : 1 });
      fetchTemplates();
    } catch (err) {
      showAlert(err.response?.data?.message || "Error actualizando estado", "error");
    }
  };

  return (
    <VuiBox>
      <AlertBanner />
      <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <VuiBox>
          <VuiTypography variant="h5" color="white" fontWeight="bold">
            Templates de mensajes
          </VuiTypography>
          <VuiTypography variant="button" color="text">
            Personaliza notificaciones con placeholders {"{{nombre}}"}, {"{{monto}}"}, {"{{fecha}}"}
          </VuiTypography>
        </VuiBox>
        <WaOutlineButton onClick={fetchTemplates} disabled={loading}>
          Actualizar
        </WaOutlineButton>
      </VuiBox>

      <Grid container spacing={2}>
        {templates.map((tpl) => (
          <Grid item xs={12} md={6} key={tpl.id}>
            <Card sx={{ p: 2.5, height: "100%" }}>
              <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <VuiBox>
                  <VuiTypography variant="h6" color="white" fontWeight="medium">
                    {tpl.nombre}
                  </VuiTypography>
                  <VuiTypography variant="caption" color="text">
                    code: {tpl.code}
                  </VuiTypography>
                </VuiBox>
                <Chip
                  label={tpl.activo ? "Activo" : "Inactivo"}
                  color={tpl.activo ? "success" : "default"}
                  size="small"
                />
              </VuiBox>
              <VuiBox
                sx={{
                  whiteSpace: "pre-wrap",
                  fontSize: "0.85rem",
                  color: "rgba(255,255,255,0.72)",
                  background: "rgba(0,0,0,0.2)",
                  borderRadius: "12px",
                  p: 1.5,
                  minHeight: 100,
                  mb: 2,
                }}
              >
                {tpl.template}
              </VuiBox>
              <VuiBox display="flex" gap={1}>
                <VuiButton color="info" variant="gradient" size="small" onClick={() => openEdit(tpl)}>
                  Editar
                </VuiButton>
                <WaOutlineButton size="small" onClick={() => toggleActive(tpl)}>
                  {tpl.activo ? "Desactivar" : "Activar"}
                </WaOutlineButton>
              </VuiBox>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={Boolean(editing)} onClose={closeEdit} fullWidth maxWidth="md">
        <DialogTitle>Editar template — {editing?.code}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Nombre"
            margin="normal"
            value={form.nombre}
            onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
          />
          <TextField
            fullWidth
            label="Contenido"
            margin="normal"
            multiline
            minRows={8}
            value={form.template}
            onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
            helperText="Variables: {{nombre}}, {{monto}}, {{fecha}}, {{deposito_id}}, {{dominio}}"
          />
          <VuiBox display="flex" alignItems="center" mt={1}>
            <Switch
              checked={form.activo}
              onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
            />
            <VuiTypography variant="button" color="text">
              Template activo
            </VuiTypography>
          </VuiBox>
          {preview && (
            <VuiBox mt={2} p={2} sx={{ background: "rgba(0,0,0,0.25)", borderRadius: "12px" }}>
              <VuiTypography variant="caption" color="text" display="block" mb={1}>
                Preview renderizado
              </VuiTypography>
              <VuiTypography variant="button" color="white" sx={{ whiteSpace: "pre-wrap" }}>
                {preview}
              </VuiTypography>
            </VuiBox>
          )}
        </DialogContent>
        <DialogActions>
          <WaOutlineButton onClick={closeEdit}>
            Cancelar
          </WaOutlineButton>
          <WaOutlineButton onClick={handlePreview} disabled={previewLoading}>
            {previewLoading ? "Generando..." : "Preview"}
          </WaOutlineButton>
          <VuiButton color="info" variant="gradient" onClick={handleSave}>
            Guardar
          </VuiButton>
        </DialogActions>
      </Dialog>
    </VuiBox>
  );
}
